import React from 'react';
import { Typography } from 'app/components/ui';
import { LabeledSection } from './LabeledSection';
import { i18n } from 'app/utils/i18n';

interface TagContentBlock {
  tagId?: string;
  value: string;
  list?: { ordered: boolean; items: string[] };
  location: { section: string; page: number };
}

const TagContentValue = ({ value, list, isActive }: { value: string; list?: { ordered: boolean; items: string[] }; isActive?: boolean }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLElement>(null);
  const [overflowing, setOverflowing] = React.useState(false);

  const isList = !!list && list.items.length > 0;

  React.useLayoutEffect(() => {
    if (open) return; // only the collapsed view is clamped/measured
    const p = ref.current?.firstElementChild as HTMLElement | null;
    if (!p) return;
    setOverflowing(p.scrollHeight > p.clientHeight + 1);
  }, [value, isList, open]);

  // Toggle appears when there's more to reveal: a multi-item list, or text that
  // overflows the single clamped line.
  const hasMore = isList ? list!.items.length > 1 || overflowing : overflowing;

  const clampStyle: React.CSSProperties = open
    ? { whiteSpace: 'normal' }
    : { display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

  const ListTag = list?.ordered ? 'ol' : 'ul';

  return (
    <div className="show-more-container" style={isActive ? { borderLeft: '3px solid #547ba2', backgroundColor: '#eaf0f7' } : undefined}>
      {isList ? (
        // Always a real <ol>/<ul> so the marker (1. / •) shows. Collapsed = first item
        // only with the first line clamped; expanded = every item.
        <ListTag style={{ margin: 0, paddingLeft: '1.25rem' }}>
          {(open ? list!.items : list!.items.slice(0, 1)).map((item, i) => (
            <li key={i} ref={!open && i === 0 ? (ref as React.RefObject<HTMLLIElement>) : undefined}>
              <Typography variant="body-5" fontWeight={isActive ? 'normal' : 'light'} style={!open && i === 0 ? clampStyle : undefined}>
                {item}
              </Typography>
            </li>
          ))}
        </ListTag>
      ) : (
        <div ref={ref as React.RefObject<HTMLDivElement>}>
          <Typography variant="body-5" fontWeight={isActive ? 'normal' : 'light'} style={clampStyle}>
            {value}
          </Typography>
        </div>
      )}
      {(hasMore || open) && (
        <button type="button" className="show-more-btn" onClick={() => setOpen((prev) => !prev)} style={{ marginTop: 4 }}>
          <Typography variant="body-3" fontWeight="semibold" color="#547ba2">
            {open ? i18n('tag.content.hide.textblock') : i18n('tag.content.show.textblock')}
          </Typography>
        </button>
      )}
    </div>
  );
};

interface TagContentBlocksProps {
  taggedValue: TagContentBlock[];
  activeTagId?: string;
}

export const TagContentBlocks = ({ taggedValue, activeTagId }: TagContentBlocksProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const blockCount = taggedValue.length;

  if (blockCount === 0) return null;

  const visibleBlocks = expanded ? taggedValue : taggedValue.slice(0, 1);

  return (
    <LabeledSection label={i18n('tag.content.title')}>
      <div className="show-more-wrapper">
        {visibleBlocks.map((block, idx) => {
          const isActive = block.tagId === activeTagId;
          return (
            <div key={idx}>
              {visibleBlocks.length > 1 && (
                <Typography variant="body-3" color="#717077" style={{ marginBottom: 4 }}>
                  {`${i18n('all.tags.page')} ${block.location.page}`}
                </Typography>
              )}
              <TagContentValue value={block.value} list={block.list} isActive={isActive} />
            </div>
          );
        })}

        {blockCount > 1 && (
          <div className="flex-row" style={{ alignItems: 'center', gap: 8 }}>
            <button type="button" className="show-more-btn" onClick={() => setExpanded((prev) => !prev)}>
              <Typography variant="body-3" fontWeight="semibold" color="#547ba2">
                {expanded ? i18n('tag.content.hide.blocks') : i18n('tag.content.show.blocks')}
              </Typography>
              <i className={`fa fa-chevron-down fa-sm ${expanded ? 'show-more' : ''}`} />
            </button>
            <Typography variant="body-3" fontWeight="light" color="#717077">
              {`${i18n('tag.content.includes')} ${blockCount} ${i18n('tag.content.text.blocks')}`}
            </Typography>
          </div>
        )}
      </div>
    </LabeledSection>
  );
};
